import {
	ButtonGroup,
	ColumnHeader,
	IColumnHeaderParams,
	IPageRange,
	PageTurnOptions,
	PagingHelper,
	PagingOptions,
	Pullout,
	PulloutType,
	SearchBox,
	Select,
	Table,
	ToolMenu,
	ToolMenuContent,
} from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import { User } from 'oidc-client';
import * as React from 'react';
import { AnyAction } from 'redux';
import styled from 'styled-components';
import ContentConstants from '../../contentConstants';
import {
	ISurveyBuilderActionCreators,
	ISurveyListActionCreators,
	ISurveyListItem,
	IValidationModel,
	SurveyPropertyUnderValidation,
	SurveyStatusType,
} from '../../models';
import { HubConnectionName } from '../../models/HubConnectionName';
import { ActionTypes } from '../../store/ActionTypes';
import { ISurveyListState } from '../../store/SurveyListStore';
import { ColumnHelper, ValidationCollectionHelper } from '../../utilities';
import { CanUserAccessFeature } from '../../utilities/FeatureFlags';
import { CardLayout, EmptyListCardContent, NoSearchResultsMessage, WarningModal } from '../common';
import { CopySurveyModal } from '../surveys/CopySurveyModal/CopySurveyModal';
import { BuildSurveyButton } from '../surveys/CopyTemplateModal/BuildSurveyButton';
import { SurveyRow } from '../surveys/SurveyRow';
import HtmlParser from 'html-react-parser';

const BuildButtonContainer = styled(ButtonGroup)`
	position: absolute;
	top: 25px;
	right: 30px;
	@media (min-width: 0) and (max-width: 768px) {
		position: relative;
		top: 12px;
		right: 0;
	}
`;
BuildButtonContainer.displayName = 'BuildButtonContainer';
const PullOutContainer = styled.div`
	margin: auto;
	max-width: 550px;
	padding-bottom: 50px;
`;
PullOutContainer.displayName = 'PullOutContainer';

const sortableColumns: string[] = ['Name', 'Status', 'Open Date', 'Create Date', 'Owner'];

export type SurveyListParams = ISurveyBuilderActionCreators &
	ISurveyListActionCreators &
	ISurveyListState & {
		user?: User;
		ConnectToSignalR: (user: User, hubConnectionName: HubConnectionName, dispatchType: AnyAction) => void;
		DisconnectSignalR: (hubConnectionName: HubConnectionName) => void;
	};
export type SurveyListState = {
	sortParams: IColumnHeaderParams;
	searchText: string;
	pageRange: IPageRange;
	isCopySurveyModalOpen: boolean;
	isDeleteSurveyModalOpen: boolean;
	selectedSurveyIdForCopy?: number;
	selectedSurveyIdForDelete?: number;
};

export class SurveyList extends React.Component<SurveyListParams, SurveyListState> {
	constructor(props: SurveyListParams) {
		super(props);

		this.state = {
			sortParams: {
				columns: ColumnHelper.GetUpdateColumnHeaders(props.columns, sortableColumns, true),
				sortedBy: 'createdOn',
				sortAsc: false,
			},
			pageRange: { offset: 0, limit: 25 },
			searchText: '',
			isCopySurveyModalOpen: false,
			isDeleteSurveyModalOpen: false,
		};

		this.HandleOnApply = this.HandleOnApply.bind(this);
		this.HandleDeleteSurvey = this.HandleDeleteSurvey.bind(this);
		this.SortOnColumn = this.SortOnColumn.bind(this);
		this.UpdateSearch = this.UpdateSearch.bind(this);
		this.UpdatePaging = this.UpdatePaging.bind(this);
		this.ToggleCopySurveyModal = this.ToggleCopySurveyModal.bind(this);
		this.ToggleDeleteSurveyModal = this.ToggleDeleteSurveyModal.bind(this);
	}

	public UNSAFE_componentWillMount() {
		this.props.GetSurveyColumns();
		this.props.GetSurveys();

		this.props.ConnectToSignalR(this.props.user!, HubConnectionName.SurveyDeleteChange, {
			type: ActionTypes.SIGNALR_SURVEY_DELETE_CHANGED,
			payload: '',
		});
	}

	public componentDidUpdate(prevProps: SurveyListParams) {
		if (this.props.columns != prevProps.columns) {
			this.setState({
				sortParams: {
					columns: ColumnHelper.GetUpdateColumnHeaders(this.props.columns, sortableColumns, true),
					sortedBy: this.state.sortParams.sortedBy,
					sortAsc: this.state.sortParams.sortAsc,
				},
			});
		}

		if (this.props.surveys.length !== prevProps.surveys.length) {
			this.setState({ ...this.state, pageRange: { ...this.state.pageRange, total: this.props.surveys.length } });
		}

		if (this.props.deleteStatusChange && this.props.deleteStatusChange != prevProps.deleteStatusChange) {
			this.props.NotifyDeleteStatus(this.props.deleteStatusChange);
		}
	}

	public componentWillUnmount() {
		this.props.DisconnectSignalR(HubConnectionName.SurveyDeleteChange);
	}

	public render() {
		const showBuildSurveyButtonInCardHeader = this.props.surveys.length > 0 || this.state.searchText !== '';
		const sortedSurveys = this.HandleSurveyListSort();
		const paging: IPageRange = { ...this.state.pageRange, total: sortedSurveys.length };
		const pagedSurveys = new PagingHelper<IValidationModel<ISurveyListItem>>().page(sortedSurveys, paging);
		const canUserAccessCopyFromTemplate = CanUserAccessFeature(this.props.user, 'EnableCopyFromTemplate');
		const hasPendingParticipants =
			this.props.surveys?.filter(
				(s) =>
					s.subject.hasPendingContact &&
					(s.subject.surveyStatus === SurveyStatusType.SchedulingInProgress ||
						s.subject.surveyStatus === SurveyStatusType.Scheduled ||
						s.subject.surveyStatus === SurveyStatusType.SurveyInProgress),
			).length > 0;

		return (
			<CardLayout
				title={ContentConstants.SURVEYLIST_PAGE_TITLE}
				header={
					showBuildSurveyButtonInCardHeader && (
						<BuildButtonContainer>
							<BuildSurveyButton
								shouldShowDropdown={canUserAccessCopyFromTemplate}
								buildNewSurvey={this.props.BuildNewSurvey}
								dropdownPositionX={0}
								dropdownPositionY={40}
							/>
						</BuildButtonContainer>
					)
				}
				loading={this.props.loading}
			>
				{(pagedSurveys.length > 0 || this.state.searchText !== '') && (
					<>
						{hasPendingParticipants && (
							<PullOutContainer>
								<Pullout type={PulloutType.Warning}>{HtmlParser(ContentConstants.PENDING_PARTICIPANTS_PULLOUT_TEXT)}</Pullout>
							</PullOutContainer>
						)}
						<PagingOptions pageRange={this.state.pageRange} updatePageRange={this.UpdatePaging} />
						<ToolMenu>
							<ToolMenuContent type={'right'}>
								<SearchBox
									callbackFunc={this.UpdateSearch}
									resultsCount={sortedSurveys.length}
									initialSearchText={this.state.searchText}
									placeholder='Search Surveys'
									inputSize={'sm'}
								/>
							</ToolMenuContent>
							<ToolMenuContent type={'left'}>
								<ButtonGroup type={'vertical'}>
									<Select
										items={this.props.columns}
										multiple={true}
										selectedValues={ColumnHelper.GetSelectedColumnNames(this.props.columns)}
										onApply={this.HandleOnApply}
										showCount={true}
										displayText={ContentConstants.COLUMNPICKER_TITLE}
										selectWidth='180px'
										optionsContainerWidth='250px'
										size='sm'
									/>
								</ButtonGroup>
							</ToolMenuContent>
						</ToolMenu>
					</>
				)}
				{pagedSurveys.length === 0 && this.state.searchText === '' && (
					<EmptyListCardContent
						instructionsTitle={ContentConstants.SURVEYLIST_INSTRUCTIONSTITLE}
						instructionsText={ContentConstants.SURVEYLIST_INSTRUCTIONSTEXT}
						canUserAccessCopyFromTemplate={canUserAccessCopyFromTemplate}
						mainButtonClick={this.props.BuildNewSurvey}
					/>
				)}
				{pagedSurveys.length > 0 && (
					<>
						<Table responsive={true} lastalign='right'>
							<ColumnHeader
								sortedBy={this.state.sortParams.sortedBy}
								sortAsc={this.state.sortParams.sortAsc}
								columns={ColumnHelper.GetUpdateColumnHeaders(this.props.columns, sortableColumns, true)}
								handleSort={this.SortOnColumn}
							/>

							<tbody>
								{pagedSurveys.map((survey: IValidationModel<ISurveyListItem>, index) => {
									const validationResults = new ValidationCollectionHelper<SurveyPropertyUnderValidation>(
										survey.validationResults,
									).GetValidationResultForProperty(SurveyPropertyUnderValidation.name);
									return (
										<SurveyRow
											validationResults={validationResults}
											CloneSurvey={this.props.CloneSurvey}
											GoToSurvey={this.props.GoToSurvey}
											GoToSurveyAdmin={this.props.GoToSurveyAdmin}
											EditSurveyTemplate={this.props.EditSurveyTemplate}
											survey={survey.subject}
											columns={this.props.columns}
											searchText={this.state.searchText}
											key={index}
											UpdateSurveyName={this.props.UpdateSurveyName}
											ClearValidation={this.props.ClearValidation}
											ToggleSurveyCopyModal={() => this.ToggleCopySurveyModal(survey.subject.surveyId)}
											ToggleSurveyDeleteModal={() => this.ToggleDeleteSurveyModal(survey.subject.surveyId)}
											user={this.props.user}
										/>
									);
								})}
							</tbody>
						</Table>
						<PageTurnOptions pageRange={this.state.pageRange} updatePageRange={this.UpdatePaging} />
						<CopySurveyModal
							isOpen={this.state.isCopySurveyModalOpen}
							onClose={() => this.ToggleCopySurveyModal(-1)}
							selectedSurveyId={this.state.selectedSurveyIdForCopy || -1}
						/>
						<WarningModal
							isOpen={this.state.isDeleteSurveyModalOpen}
							contentHeadline={ContentConstants.WARNING_MODAL_DELETE_HEADLINE}
							contentBody={ContentConstants.WARNING_MODAL_DELETESURVEY_BODY}
							onSubmit={() => this.HandleDeleteSurvey(this.state.selectedSurveyIdForDelete || -1)}
							onClose={() => this.ToggleDeleteSurveyModal(-1)}
						/>
					</>
				)}
				{!pagedSurveys.length && this.state.searchText && <NoSearchResultsMessage />}
			</CardLayout>
		);
	}

	public HandleOnApply(values?: any) {
		const columns = ColumnHelper.UpdateSelectedColumns(this.props.columns, values);
		this.props.UpdateColumns(columns);
	}

	public HandleSurveyListSort() {
		const filteredSurveys = this.FilterSurveysBySearchText(this.state.searchText);
		const sortAsc: ['asc' | 'desc'] = this.state.sortParams.sortAsc ? ['asc'] : ['desc'];
		const stringSort = (attribute: string) => (item: IValidationModel<ISurveyListItem>) => _.get(item, attribute).toLowerCase();
		const dateSort = (attribute: string) => (item: IValidationModel<ISurveyListItem>) => _.get(item, attribute);
		let sortRule: (item: IValidationModel<ISurveyListItem>) => any = dateSort('subject.createdOn');
		let sortAttribute = 'subject.';
		switch (this.state.sortParams.sortedBy) {
			case 'Name':
				sortAttribute = sortAttribute + 'name';
				sortRule = stringSort(sortAttribute);
				break;
			case 'Status':
				sortAttribute = sortAttribute + 'surveyStatus';
				sortRule = (item: IValidationModel<ISurveyListItem>) => SurveyStatusType[_.get(item, sortAttribute)];
				break;
			case 'Open Date':
				sortAttribute = sortAttribute + 'openDate';
				sortRule = dateSort(sortAttribute);
				break;
			case 'Create Date':
				sortAttribute = sortAttribute + 'createdOn';
				sortRule = dateSort(sortAttribute);
				break;
			case 'Owner':
				sortAttribute = sortAttribute + 'owner';
				sortRule = stringSort(sortAttribute);
				break;
			default:
				break;
		}
		return _.orderBy([...filteredSurveys], [sortRule], sortAsc);
	}

	public FilterSurveysBySearchText(searchText: string) {
		return this.props.surveys.filter((s) => {
			return s.subject.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
		});
	}

	public SortOnColumn(sortedBy: string, sortAscByDefault: boolean) {
		let sortAsc = !this.state.sortParams.sortAsc;
		if (this.state.sortParams.sortedBy !== sortedBy) {
			sortAsc = sortAscByDefault;
		}
		this.setState({
			sortParams: {
				columns: ColumnHelper.GetUpdateColumnHeaders(this.props.columns, sortableColumns, sortAscByDefault),
				sortedBy: sortedBy,
				sortAsc: sortAsc,
			},
		});
	}

	public UpdateSearch(searchText: string) {
		const filteredSurveys = this.FilterSurveysBySearchText(searchText);
		const pageRangeOffset = this.state.pageRange.offset > filteredSurveys.length ? 0 : this.state.pageRange.offset;
		this.setState({
			...this.state,
			searchText: searchText,
			pageRange: { ...this.state.pageRange, offset: pageRangeOffset, total: filteredSurveys.length },
		});
	}

	public UpdatePaging(pageRange: IPageRange) {
		this.setState({ ...this.state, pageRange: pageRange });
	}

	public ToggleCopySurveyModal(selectedSurveyId?: number) {
		this.setState({ ...this.state, isCopySurveyModalOpen: !this.state.isCopySurveyModalOpen, selectedSurveyIdForCopy: selectedSurveyId });
	}

	public ToggleDeleteSurveyModal(selectedSurveyId?: number) {
		this.setState({
			...this.state,
			isDeleteSurveyModalOpen: !this.state.isDeleteSurveyModalOpen,
			selectedSurveyIdForDelete: selectedSurveyId,
		});
	}

	public HandleDeleteSurvey(selectedSurveyId?: number) {
		if (selectedSurveyId && selectedSurveyId > 0) {
			this.props.DeleteSurvey(selectedSurveyId);
		}
		this.setState({ ...this.state, isDeleteSurveyModalOpen: !this.state.isDeleteSurveyModalOpen });
	}
}
